import { create } from "zustand";

type User = {
  email: string;
  icon: string;
  team: string;
  telegram: string;
  whatsapp: string;
  viber: string;
  userId: string;
  wallet: number;
  bonusWallet: number;
  isAdmin: boolean;
  onboardingCompleted: boolean;
  referralCode: string;
  creatives: [];
};

type unverifiedUserInfo = {
  userId: string | null;
  verificationMode: "telegram" | "whatsapp" | "viber" | null;
};

interface AuthState {
  user: User | null;
  unverifiedUserInfo: unverifiedUserInfo;
  isUserAuthenticated: boolean;
  setUser: (user: User | null) => void;
  setUverifiedUserInfo: (userInfo: unverifiedUserInfo) => void;
  setAuthenticated: (isAuthenticated: boolean) => void;
  clearAuthData: () => void;
  updateUserData: (user: Partial<User>) => void;
  getUserAuthStatus: () => boolean;
}

export const useAuthStore = create<AuthState>((set, get) => {
  const savedUserString = typeof window !== "undefined" && localStorage.getItem("userData");
  const savedUser = savedUserString ? JSON.parse(savedUserString || "{}") : null;
  const isUserAuthenticated = !!savedUser;

  return {
    user: savedUser,
    unverifiedUserInfo: { userId: null, verificationMode: null },
    isUserAuthenticated,
    setUser: (user) => {
      set({ user, isUserAuthenticated: !!user });
      if (user) {
        localStorage.setItem("userData", JSON.stringify(user));
      } else {
        localStorage.removeItem("userData");
      }
    },
    setUverifiedUserInfo: (userInfo) => set({ unverifiedUserInfo: userInfo }),
    updateUserData: (user) => {
      const currentUser = get().user;
      if (!currentUser) return;

      const updatedUser: User = {
        email: currentUser.email,
        icon: currentUser.icon,
        team: currentUser.team,
        telegram: currentUser.telegram,
        whatsapp: currentUser.whatsapp,
        viber: currentUser.viber,
        userId: currentUser.userId,
        wallet: currentUser.wallet,
        bonusWallet: currentUser.bonusWallet,
        isAdmin: currentUser.isAdmin,
        onboardingCompleted: currentUser.onboardingCompleted,
        referralCode: currentUser.referralCode,
        creatives: currentUser.creatives,
        ...user,
      };

      set({ user: updatedUser });
      localStorage.setItem("userData", JSON.stringify(updatedUser));
    },
    setAuthenticated: (isAuthenticated) => {
      set({ isUserAuthenticated: isAuthenticated });
    },
    clearAuthData: () => {
      set({ user: null, isUserAuthenticated: false });
      localStorage.removeItem("authToken");
      localStorage.removeItem("userData");
    },

    getUserAuthStatus: () => get().isUserAuthenticated,
  };
});
